import { CSSProperties, useContext, useEffect, useState } from "react"
import { pageView } from "analytics"
import { UpgradeModalContext } from "Hooks/UpgradeModalContext"
import useOrganizationV2 from 'Hooks/useOrganizationV2'
import useAppView from 'Hooks/useAppView'
import lowerCase from "lodash/lowerCase"
import startCase from "lodash/startCase"
import { useLocation } from "react-router-dom"
import { useViewOnlyAccess, ViewOnlyFeature } from "Hooks/useViewOnlyAccess"
import { getStatusToShowPaymentFailedBanner } from "modules/Utils"
import AccountTab from "./Components/AccountTab/AccountTab"
import InvoicesTabV2 from "./Components/InvoicesTabV2"
import OrganizationSubscriptionStatusBanner from "components/shareable/OrganizationSubscriptionStatusBanner"
import ButtonSymphony from "elements/ButtonSymphony"
import PageSymphony from "elements/PageSymphony"
import useBrand, { BrandExistsState } from "Hooks/useBrand"
import DebugBox from "components/shareable/DebugBox"
import CardSymphony from "elements/CardSymphony"
import TabsSymphony, { TabItem } from "elements/TabsSymphony"
import useScreen from "Hooks/useScreen"
import TabSymphony from "elements/TabSymphony"
import Sty from "modules/Sty"
import PrivacyAndTerms from "components/shareable/PrivacyAndTerms"
import Icon from "elements/Icon"
import Banner from "elements/Banner"
import BillingTabV2 from "./Components/BillingTabV2"

type GlobalWindow = Window & {
    Intercom: (type: string) => void
}

enum PageTabs {
    ACCOUNT = "ACCOUNT",
    BILLING = "BILLING",
    INVOICES = "INVOICES",
    PRIVACY_AND_TERMS = "PRIVACY_AND_TERMS",
}

enum PageTabsViewOnly {
    ACCOUNT = "ACCOUNT",
}

// Create new organization tabs array
const ORGANIZATION_TABS: TabItem[] = [
    PageTabs.ACCOUNT,
].map((tab) => ({
    id: tab,
    title: startCase(lowerCase(tab)),
}))

const SETTINGS_TABS: TabItem[] = [
    PageTabs.ACCOUNT,
    PageTabs.BILLING,
    PageTabs.INVOICES,
    PageTabs.PRIVACY_AND_TERMS,
].map((tab) => ({
    id: tab,
    title: startCase(lowerCase(tab)),
}))

const viewOnlyTabs = [
    PageTabsViewOnly.ACCOUNT,
].map((tab) => ({
    value: tab,
    name: startCase(lowerCase(tab)),
}))

const SettingsPage = () => {
    const {
        mobileView,
    } = useScreen()
    const { currentBrand, isBrandAdmin } = useBrand() as BrandExistsState
    const { handleOpenModal } = useContext(UpgradeModalContext);
    const { isTeamView } = useAppView()
    const { organization } = useOrganizationV2()

    // Check View Only access for settings features
    const { hasAccess: canAccessBilling } = useViewOnlyAccess(ViewOnlyFeature.SETTINGS_BILLING)
    const { hasAccess: canAccessInvoices } = useViewOnlyAccess(ViewOnlyFeature.SETTINGS_INVOICES)

    const showPaymentFailedBanner = !isTeamView() ? getStatusToShowPaymentFailedBanner(currentBrand) : false
    let defaultTab: TabItem = SETTINGS_TABS[0];

    // Helper function to check if brand belongs to organization
    const brandIdIsInOrganization = (brandId: number) => {
        return organization?.brands?.some(brand => brand.id === brandId) || false
    }

    // used to check if the brand belongs to the organization
    // so we can render either organization-specific View, or regular pro/free view
    const inOrganization = !isTeamView() ? brandIdIsInOrganization(currentBrand?.id) : true
    defaultTab = SETTINGS_TABS[0];

    /**
     * Filter tabs based on context and permissions:
     * - Team View: All tabs visible (billing/invoices managed at team level)
     * - Brand View (Team Brand): Hide billing/invoices (managed at team level)
     * - Brand View (Individual): All tabs visible (managed at brand level)
     * - View Only Permissions: Hide restricted tabs based on user permissions
     */
    const getVisibleSettingsTabs = () => {
        const isBrandViewOfTeamBrand = !isTeamView() && inOrganization;
        
        return SETTINGS_TABS.filter(tab => {
            // Hide billing/invoices for brand view of team brands (managed at team level)
            if ((tab.id === PageTabs.BILLING || tab.id === PageTabs.INVOICES) && isBrandViewOfTeamBrand) {
                return false;
            }
            
            // Filter based on View Only permissions
            if (tab.id === PageTabs.BILLING && !canAccessBilling) {
                return false;
            }
            if (tab.id === PageTabs.INVOICES && !canAccessInvoices) {
                return false;
            }
            
            return true;
        });
    };

    const visibleSettingsTabs = getVisibleSettingsTabs();
    const [selectedSettingsTab, setSelectedSettingsTab] = useState<TabItem>(visibleSettingsTabs[0] || defaultTab);
    const handleClickSupportButton = () => {
        const globalWindow = window as unknown as GlobalWindow
        if (globalWindow.Intercom) {
            globalWindow.Intercom("show")
        }
    }

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabFromQuery = queryParams.get("tab");

    useEffect(() => {
        if (tabFromQuery && Object.values(PageTabs).includes(tabFromQuery as PageTabs)) {
            const requestedTab = SETTINGS_TABS.find((tab) => tab.id === tabFromQuery);
            const isTabVisible = visibleSettingsTabs.some(tab => tab.id === tabFromQuery);
            
            if (requestedTab && isTabVisible) {
                setSelectedSettingsTab(requestedTab);
            } else {
                // Tab exists but is hidden in current context - default to first visible tab
                setSelectedSettingsTab(visibleSettingsTabs[0]);
            }
        } else if (tabFromQuery === 'ADVANCED_SETTINGS' || tabFromQuery === 'CONNECTIONS') {
            // Backward compatibility: redirect old ADVANCED_SETTINGS and CONNECTIONS to account tab
            setSelectedSettingsTab(SETTINGS_TABS.find((tab) => tab.id === PageTabs.ACCOUNT) as TabItem);
        }
    }, [tabFromQuery, visibleSettingsTabs]);

    useEffect(() => {
        pageView("Settings");
    }, []);

    const renderAccountTab = () => {
        return (
            <TabSymphony className="settingsPageTabAccount" visible={selectedSettingsTab.id === PageTabs.ACCOUNT}>
                <AccountTab />
            </TabSymphony>
        )
    }

    const renderBillingTab = () => {
        return (
            <TabSymphony className="settingsPageTabBilling" visible={selectedSettingsTab.id === PageTabs.BILLING}>
                {canAccessBilling ? (
                    <BillingTabV2 />
                ) : (
                    <Banner
                        className="settingsViewOnlyBillingBanner"
                        title='This tab is disabled for members with "View Only" access'
                        description="Ask your team admin to give you full admin access to this brand to view billing settings."
                        color="yellow"
                    />
                )}
            </TabSymphony>
        )
    }

    const renderInvoicesTab = () => {
        return (
            <TabSymphony className="settingsPageTabInvoices" visible={selectedSettingsTab.id === PageTabs.INVOICES}>
                {canAccessInvoices ? (
                    <InvoicesTabV2 />
                ) : (
                    <Banner
                        className="settingsViewOnlyInvoicesBanner"
                        title='This tab is disabled for members with "View Only" access'
                        description="Ask your team admin to give you full admin access to this brand to view invoices."
                        color="yellow"
                    />
                )}
            </TabSymphony>
        )
    }

    const renderPrivacyPolicyTab = () => {
        return (
            <TabSymphony className="settingsPageTabPrivacyPolicy" visible={selectedSettingsTab.id === PageTabs.PRIVACY_AND_TERMS}>
                <PrivacyAndTerms />
            </TabSymphony>
        )
    }

    const getSettingsSubtitle = () => {
        const isBrandViewOfTeamBrand = !isTeamView() && inOrganization;
        
        if (isTeamView()) {
            return "Manage your team's account settings, billing, and invoices.";
        } else if (isBrandViewOfTeamBrand) {
            return "Manage your brand's account settings. Billing and invoices are managed at the team level.";
        } else {
            return "Manage your account settings and preferences.";
        }
    };

    return (
        <PageSymphony className="settingsPageComponent" isContent>
            <CardSymphony
                className="settingsPageCard"
                title={"Settings"}
                subtitle={getSettingsSubtitle()}
            >
                <TabsSymphony
                    className="settingsPageTabs"
                    selectedTab={selectedSettingsTab}
                    onChange={(tab) => setSelectedSettingsTab(tab as TabItem)}
                    tabs={visibleSettingsTabs}
                    isBorderBottom
                    rightSideContent={!mobileView &&
                        <ButtonSymphony
                            className="settingsPageSupportButton"
                            onClick={handleClickSupportButton}
                        >
                            Support / Help
                        </ButtonSymphony>
                    }
                />
                {renderAccountTab()}
                {renderBillingTab()}
                {renderInvoicesTab()}
                {renderPrivacyPolicyTab()}
            </CardSymphony>

        </PageSymphony>
    )
}

const sty: Record<string, CSSProperties> = {}

export default SettingsPage
